import { Printer } from "lucide-react";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function ReceiptDialog({ open, onOpenChange, receipt }) {
  const { t, fmtCurrency, fmtDate } = useI18n();
  if (!receipt) return null;

  const print = () => {
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    const rows = (receipt.lines || []).map((l) => `
      <tr><td>${l.quantity}× ${l.description || ""}</td><td style="text-align:right">${Number(l.line_gross).toFixed(2)}</td></tr>
      <tr><td colspan="2" style="color:#666;font-size:11px">${l.sku || ""} · ${Number(l.unit_price).toFixed(2)} · MwSt ${l.vat_rate}%</td></tr>`).join("");
    w.document.write(`<html><head><title>${receipt.receipt_number}</title>
      <style>body{font-family:monospace;padding:12px;color:#111} h2{margin:0} table{width:100%;border-collapse:collapse} td{padding:2px 0} hr{border:none;border-top:1px dashed #999;margin:8px 0} .tot{display:flex;justify-content:space-between;font-weight:bold}</style>
      </head><body>
      <h2>${receipt.seller?.name || ""}</h2>
      <div style="font-size:11px;color:#666">${receipt.seller?.city || ""} · ${receipt.seller?.vat_id || ""}</div>
      <hr/>
      <div style="font-size:12px">${t("pos.receipt_number")}: <b>${receipt.receipt_number}</b><br/>${fmtDate(receipt.date)} · ${receipt.customer || ""}</div>
      <hr/>
      <table>${rows}</table>
      <hr/>
      <div class="tot"><span>${t("pos.net")}</span><span>${Number(receipt.net_total).toFixed(2)}</span></div>
      <div class="tot"><span>${t("pos.tax")}</span><span>${Number(receipt.tax_total).toFixed(2)}</span></div>
      <div class="tot" style="font-size:16px"><span>${t("pos.total")}</span><span>${Number(receipt.gross_total).toFixed(2)} ${receipt.currency}</span></div>
      <div class="tot" style="font-weight:400"><span>${t("pos.pay")}</span><span>${Number(receipt.paid_total).toFixed(2)}</span></div>
      <div class="tot" style="font-weight:400"><span>${t("pos.change_due")}</span><span>${Number(receipt.change_due).toFixed(2)}</span></div>
      <hr/><div style="text-align:center;font-size:11px;color:#666">Mayr-Technologies</div>
      </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pos-receipt-dialog">
        <DialogHeader><DialogTitle>{t("pos.receipt")} — {receipt.receipt_number}</DialogTitle></DialogHeader>
        <div className="rounded-md border border-dashed border-slate-300 p-4 font-mono text-sm dark:border-slate-600" data-testid="pos-receipt-body">
          <div className="text-center font-bold">{receipt.seller?.name}</div>
          <div className="mb-2 text-center text-xs text-slate-500">{fmtDate(receipt.date)} · {receipt.customer}</div>
          <div className="space-y-1">
            {(receipt.lines || []).map((l, i) => (
              <div key={i} className="flex justify-between">
                <span className="truncate">{l.quantity}× {l.description}</span>
                <span className="tabular-nums">{fmtCurrency(l.line_gross)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 border-t border-dashed pt-2">
            <div className="flex justify-between"><span>{t("pos.net")}</span><span className="tabular-nums">{fmtCurrency(receipt.net_total)}</span></div>
            <div className="flex justify-between"><span>{t("pos.tax")}</span><span className="tabular-nums">{fmtCurrency(receipt.tax_total)}</span></div>
            <div className="flex justify-between text-base font-bold"><span>{t("pos.total")}</span><span className="tabular-nums">{fmtCurrency(receipt.gross_total)}</span></div>
            <div className="flex justify-between text-slate-500"><span>{t("pos.change_due")}</span><span className="tabular-nums">{fmtCurrency(receipt.change_due)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-receipt-close">{t("common.close")}</Button>
          <Button onClick={print} data-testid="pos-receipt-print"><Printer className="mr-2 h-4 w-4" />{t("pos.print_receipt")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
